import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Spinner, Empty } from "@/components/Common";
import { JobCard } from "@/components/JobCard";
import { JobDetailSheet } from "@/components/JobDetailSheet";
import { ReadyFilter, useReadyToggle } from "@/components/ReadyFilter";
import { isReady, useAtsTick } from "@/lib/atsScores";
import { api, asList, enc, errMsg } from "@/lib/api";

export default function Saved() {
  const [apps, setApps] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [ready, setReady] = useReadyToggle("saved");
  const passMin = useAtsTick();
  const load = () => api.get("/applications").then((r) => setApps(asList(r.data, "applications"))).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const remove = async (job) => {
    try { await api.delete(`/applications/${enc(job.id)}`); setApps((a) => a.filter((x) => x.job_id !== job.id)); toast.success("Removed"); } catch (e) { toast.error(errMsg(e)); }
  };

  const saved = (apps || []).filter((a) => a.status === "saved");
  const shown = ready ? saved.filter((a) => isReady(a.job_id, passMin)) : saved;
  return (
    <div>
      <PageHeader title="Saved Jobs" subtitle="Roles you bookmarked. Move them through your pipeline from the tracker.">
        <Link to="/tracker"><Button variant="outline" data-testid="go-tracker-button">Open tracker</Button></Link>
      </PageHeader>
      {!apps ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : saved.length === 0 ? (
        <Empty icon={Bookmark} title="Nothing saved yet" text="Bookmark roles from Job Search to keep them here."><Link to="/search"><Button data-testid="empty-search-button">Find jobs</Button></Link></Empty>
      ) : (
        <>
          <div className="mb-4"><ReadyFilter on={ready} onChange={setReady} min={passMin} count={shown.length} /></div>
          {shown.length === 0 && <Empty icon={CircleCheck} title="No ready jobs yet" text={`No saved job has an ATS score of ${passMin}% or more yet.`} />}
          <div className="grid gap-4 xl:grid-cols-2" data-testid="saved-list">
            {shown.map((a, i) => <JobCard key={a.job_id} index={i} job={{ ...(a.job || {}), id: a.job_id, status: a.status }} onOpen={() => setOpenId(a.job_id)} onToggleSave={remove} tailor />)}
          </div>
        </>
      )}
      <JobDetailSheet jobId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
